import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams } from "react-router-dom";
import useReveal from "@/hooks/useReveal";
import { AuthProvider } from "@/contexts/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import { ConfirmProvider } from "@/components/ConfirmProvider";
import { Toaster } from "@/components/ui/sonner";

// Landing
import Navbar from "@/components/wishire/Navbar";
import Hero from "@/components/wishire/Hero";
import Problem from "@/components/wishire/Problem";
import WishireScoreSection from "@/components/wishire/WishireScoreSection";
import CollegeFeatures from "@/components/wishire/CollegeFeatures";
import BookDemo from "@/components/wishire/BookDemo";
import LinkedInLead from "@/components/wishire/LinkedInLead";
import CandidateFeatures from "@/components/wishire/CandidateFeatures";
import HowItWorks from "@/components/wishire/HowItWorks";
import LiveDemo from "@/components/wishire/LiveDemo";
import Trust from "@/components/wishire/Trust";
import Pricing from "@/components/wishire/Pricing";
import Faq from "@/components/wishire/Faq";
import Waitlist from "@/components/wishire/Waitlist";
import Footer from "@/components/wishire/Footer";

// Auth + onboarding
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import ProtectedRoute from "@/pages/ProtectedRoute";
import OnboardingGate from "@/pages/OnboardingGate";
import RoleChooser from "@/pages/onboarding/RoleChooser";
import CandidateProfileSetup from "@/pages/onboarding/CandidateProfileSetup";
import PublicProfile from "@/pages/PublicProfile";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import PublicJobPage from "@/pages/public/PublicJobPage";
import SubmissionDossier from "@/pages/public/SubmissionDossier";
import SitePage from "@/pages/public/SitePage";
import ContentPage, { GuidesIndex } from "@/pages/public/ContentPage";
import RecruiterShortlist from "@/pages/public/RecruiterShortlist";

// Admin
import AdminLayout from "@/pages/admin/AdminLayout";
import {
  AdminOverview,
  AdminUsers,
  AdminCompanies,
  AdminAgencies,
  AdminColleges,
  AdminPricing,
  AdminWaitlist,
  AdminJobs,
  AdminLeads,
  AdminContacts,
} from "@/pages/admin/AdminPages";
import AdminSales from "@/pages/admin/AdminSales";
import AdminSitePages from "@/pages/admin/AdminSitePages";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminIntegrations from "@/pages/admin/AdminIntegrations";
import AdminLeadCRM from "@/pages/admin/AdminLeadCRM";
import AdminRecruiters from "@/pages/admin/AdminCompanies";
import AdminCourses from "@/pages/admin/AdminCourses";
import CompaniesPage from "@/pages/app/CompaniesPage";
import LinkedInCoachPage from "@/pages/app/LinkedInCoachPage";
import CoursePage from "@/pages/app/CoursePage";
import VerifyCertificate from "@/pages/VerifyCertificate";
import CompanyHubPage from "@/pages/app/CompanyHubPage";

// Candidate
import AppLayout from "@/pages/app/AppLayout";
import Dashboard from "@/pages/app/Dashboard";
import WishAgentPage from "@/pages/app/WishAgentPage";
import { JobApply } from "@/pages/app/JobsCandidate";
import TakeAssessment from "@/pages/app/TakeAssessment";
import RejectionCoachingPage from "@/pages/app/RejectionCoachingPage";
import ReferralPage from "@/pages/app/ReferralPage";
// Consolidated candidate hubs (each composes existing pages behind in-page tabs)
import {
  ResumeHub,
  InterviewHub,
  CoachHub,
  JobsHub,
  ApplicationsHub,
  ScoreHub,
  ProfileHub,
} from "@/pages/app/AppHubs";

// Company
import CompanyLayout from "@/pages/company/CompanyLayout";
import CompanyOnboarding from "@/pages/company/CompanyOnboarding";
import CompanyDashboard from "@/pages/company/CompanyDashboard";
import { JobsList, JobNew, JobDetail } from "@/pages/company/Jobs";
import TalentPool from "@/pages/company/TalentPool";
import CampusHiring from "@/pages/company/CampusHiring";
import { AssessmentsList, AssessmentNew, AssessmentSubmissions } from "@/pages/company/Assessments";
import CompanySettings from "@/pages/company/CompanySettings";
import CompanyMessages from "@/pages/company/CompanyMessages";
import Connectors from "@/pages/company/Connectors";
import Schedule from "@/pages/company/Schedule";
import Analytics from "@/pages/company/Analytics";
import Scorecards from "@/pages/company/Scorecards";
import Billing from "@/pages/company/Billing";

// Agency
import AgencyLayout from "@/pages/agency/AgencyLayout";
import { AgencyOnboarding, AgencyDashboard, AgencyClients, AgencyTalent, AgencyJobs, AgencySubmissions } from "@/pages/agency/AgencyPages";
import AgencyBilling from "@/pages/agency/AgencyBilling";
// Campus (placement cell)
import CampusLayout from "@/pages/campus/CampusLayout";
import { CampusOnboarding, CampusDashboard, CampusStudents, CampusDrives, CampusReports, CampusAnnouncements, CampusTeam, CampusRecruiters, CampusProfile, CampusAgent } from "@/pages/campus/CampusPages";
import CampusPlaybook from "@/pages/campus/CampusPlaybook";
import AlumniPage from "@/pages/campus/AlumniPage";
import CampusDrivesPage from "@/pages/app/CampusDrivesPage";
import CompanyTestPage from "@/pages/app/CompanyTestPage";
import DriveAssessmentPage from "@/pages/app/DriveAssessmentPage";
import DrivePrepPage from "@/pages/app/DrivePrepPage";
import SchedulePage from "@/pages/public/SchedulePage";
import MessagesPage from "@/pages/app/MessagesPage";

function Landing() {
  const rootRef = useReveal();
  return (
    <div className="App" ref={rootRef} data-testid="wishire-landing-root">
      <Navbar />
      <main>
        <Hero />
        <Problem />
        <CollegeFeatures />
        <WishireScoreSection />
        <CandidateFeatures />
        <HowItWorks />
        <BookDemo />
        <Trust />
        <Faq />
      </main>
      <Footer />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/u/:handle" element={<PublicProfile />} />
      <Route path="/verify/:id" element={<VerifyCertificate />} />
      <Route path="/jobs/:id" element={<PublicJobPage />} />
      <Route path="/s/:token" element={<SubmissionDossier />} />
      <Route path="/d/:token" element={<RecruiterShortlist />} />
      <Route path="/schedule/:token" element={<SchedulePage />} />
      <Route path="/p/:slug" element={<SitePage />} />
      {/* SEO content pages (content/seo-pages.js; prerendered at build) */}
      <Route path="/guides" element={<GuidesIndex />} />
      {["/for-colleges", "/for-students", "/for-recruiters", "/pricing", "/about", "/guides/:slug", "/company-tests", "/company-tests/:slug", "/compare/:slug"].map((p) => (
        <Route key={p} path={p} element={<ContentPage />} />
      ))}

      {/* Admin workspace */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminOverview />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="companies" element={<AdminCompanies />} />
        <Route path="agencies" element={<AdminAgencies />} />
        <Route path="colleges" element={<AdminColleges />} />
        <Route path="pricing" element={<AdminPricing />} />
        <Route path="jobs" element={<AdminJobs />} />
        <Route path="waitlist" element={<AdminWaitlist />} />
        <Route path="leads" element={<AdminLeads />} />
        <Route path="contacts" element={<AdminContacts />} />
        <Route path="sales" element={<AdminSales />} />
        <Route path="pages" element={<AdminSitePages />} />
        <Route path="audit" element={<AdminAudit />} />
        <Route path="integrations" element={<AdminIntegrations />} />
        <Route path="crm" element={<AdminLeadCRM />} />
        <Route path="recruiters" element={<AdminRecruiters />} />
        <Route path="courses" element={<AdminCourses />} />
      </Route>
      {/* Onboarding (auth required, no gate — these ARE the onboarding) */}
      <Route
        path="/onboarding/role"
        element={
          <ProtectedRoute>
            <RoleChooser />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/profile"
        element={
          <ProtectedRoute>
            <CandidateProfileSetup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/company/onboarding"
        element={
          <ProtectedRoute>
            <CompanyOnboarding />
          </ProtectedRoute>
        }
      />

      {/* Candidate workspace */}
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <OnboardingGate expect="candidate">
              <AppLayout />
            </OnboardingGate>
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />

        {/* ── Consolidated hubs (in-page tabs) ── */}
        <Route path="resume" element={<ResumeHub />} />
        <Route path="interview" element={<InterviewHub />} />
        <Route path="coach" element={<CoachHub />} />
        <Route path="linkedin" element={<LinkedInCoachPage />} />
        <Route path="courses/:id" element={<CoursePage />} />
        <Route path="jobs" element={<JobsHub />} />
        <Route path="applications" element={<ApplicationsHub />} />
        <Route path="score-insights" element={<ScoreHub />} />
        <Route path="profile" element={<ProfileHub />} />

        {/* ── Standalone ── */}
        <Route path="campus" element={<CampusDrivesPage />} />
        <Route path="wishagent" element={<WishAgentPage />} />
        <Route path="company-tests" element={<CompanyTestPage />} />
        <Route path="campus/drives/:driveId/assessment" element={<DriveAssessmentPage />} />
        <Route path="campus/drives/:driveId/prep" element={<DrivePrepPage />} />
        <Route path="jobs/:id" element={<JobApply />} />
        <Route path="assessment/:id" element={<TakeAssessment />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="referrals" element={<ReferralPage />} />
        <Route path="coaching/rejection/:applicationId" element={<RejectionCoachingPage />} />

        {/* ── Legacy paths → redirect into the merged hub tab ── */}
        <Route path="companies" element={<CompaniesPage />} />
        <Route path="companies/:slug" element={<CompanyHubPage />} />
        <Route path="resume-builder" element={<Navigate to="/app/resume?tab=build" replace />} />
        <Route path="practice" element={<Navigate to="/app/interview?tab=practice" replace />} />
        <Route path="linkedin-coach" element={<Navigate to="/app/linkedin" replace />} />
        <Route path="learn" element={<Navigate to="/app/coach?tab=plan" replace />} />
        <Route path="skills" element={<Navigate to="/app/coach?tab=plan" replace />} />
        <Route path="roadmap" element={<Navigate to="/app/coach?tab=plan" replace />} />
        <Route path="prep" element={<Navigate to="/app/interview?tab=practice" replace />} />
        <Route path="badges" element={<Navigate to="/app/profile?tab=badges" replace />} />
        <Route path="salary" element={<Navigate to="/app/score-insights?tab=salary" replace />} />
        <Route path="jobs/matched" element={<Navigate to="/app/jobs?tab=foryou" replace />} />
        <Route path="applications/transparency" element={<Navigate to="/app/applications?tab=standing" replace />} />
      </Route>

      {/* Company workspace */}
      <Route
        path="/company"
        element={
          <ProtectedRoute>
            <OnboardingGate expect="company">
              <CompanyLayout />
            </OnboardingGate>
          </ProtectedRoute>
        }
      >
        <Route index element={<CompanyDashboard />} />
        <Route path="jobs" element={<JobsList />} />
        <Route path="jobs/new" element={<JobNew />} />
        <Route path="jobs/:id" element={<JobDetail />} />
        <Route path="talent" element={<TalentPool />} />
        <Route path="campus" element={<CampusHiring />} />
        <Route path="assessments" element={<AssessmentsList />} />
        <Route path="assessments/new" element={<AssessmentNew />} />
        <Route path="assessments/:id" element={<AssessmentSubmissions />} />
        <Route path="settings" element={<CompanySettings />} />
        <Route path="messages" element={<CompanyMessages />} />
        <Route path="connectors" element={<Connectors />} />
        <Route path="schedule" element={<Schedule />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="scorecards" element={<Scorecards />} />
        <Route path="billing" element={<Billing />} />
      </Route>

      {/* Agency onboarding */}
      <Route
        path="/agency/onboarding"
        element={<ProtectedRoute><AgencyOnboarding /></ProtectedRoute>}
      />
      {/* Agency workspace */}
      <Route
        path="/agency"
        element={
          <ProtectedRoute>
            <OnboardingGate expect="agency">
              <AgencyLayout />
            </OnboardingGate>
          </ProtectedRoute>
        }
      >
        <Route index element={<AgencyDashboard />} />
        <Route path="clients" element={<AgencyClients />} />
        <Route path="talent" element={<AgencyTalent />} />
        <Route path="jobs" element={<AgencyJobs />} />
        <Route path="submissions" element={<AgencySubmissions />} />
        <Route path="billing" element={<AgencyBilling />} />
      </Route>

      {/* Campus (placement cell) onboarding */}
      <Route
        path="/campus/onboarding"
        element={<ProtectedRoute><CampusOnboarding /></ProtectedRoute>}
      />
      {/* Campus workspace */}
      <Route
        path="/campus"
        element={
          <ProtectedRoute>
            <OnboardingGate expect="college">
              <CampusLayout />
            </OnboardingGate>
          </ProtectedRoute>
        }
      >
        <Route index element={<CampusDashboard />} />
        <Route path="students" element={<CampusStudents />} />
        <Route path="drives" element={<CampusDrives />} />
        <Route path="ai" element={<CampusAgent />} />
        <Route path="alumni" element={<AlumniPage />} />
        <Route path="recruiters" element={<CampusRecruiters />} />
        <Route path="announcements" element={<CampusAnnouncements />} />
        <Route path="reports" element={<CampusReports />} />
        <Route path="playbook" element={<CampusPlaybook />} />
        <Route path="team" element={<CampusTeam />} />
        <Route path="profile" element={<CampusProfile />} />
      </Route>

      {/* Unknown URLs render blank without this — send them home. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <ConfirmProvider>
            <AppRouter />
            <Toaster position="top-center" richColors closeButton />
          </ConfirmProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
